import type { RefObject } from 'react';
import React from 'react';
import { type Theme, useStyletron } from 'baseui';
import { useAtomValue } from 'jotai';
import type { StyleObject } from 'styletron-react';
import { currentFrameAtom } from '../../atoms/video-playback-atoms';
import { CELL_WIDTH, MARKER_CURSOR, RULER_HEADER_HEIGHT, TIMELINE_DRAG_ATTR, TIMELINE_HEIGHT } from '../../constants/frame-timeline-v4';
import { formatFrameBadge, getMarkerTransform } from '../../utils/frame-timeline-v4';
type PropsT = {
  totalFrames: number;
  cellWidth?: number;
  /** So the drag hook can tell whether a gesture started on this handle. */
  badgeRef?: RefObject<HTMLDivElement>;
};
const MARKER_STYLE: StyleObject = {
  position: 'absolute',
  top: '0px',
  left: '0px',
  width: '0px',
  height: '100%'
};
const STICKY_LAYER_STYLE: StyleObject = {
  position: 'sticky',
  top: '0px',
  width: '0px',
  height: `${TIMELINE_HEIGHT}px`
};
const getLineStyle = (theme: Theme): StyleObject => ({
  position: 'absolute',
  top: `${RULER_HEADER_HEIGHT}px`,
  bottom: '0px',
  left: '0px',
  width: theme.sizing.scale0,
  transform: 'translateX(-50%)',
  backgroundColor: theme.colors.brandContentPrimary
});
const getBadgeStyle = (theme: Theme): StyleObject => ({
  ...theme.typography.LabelXSmall,
  position: 'absolute',
  top: `calc(${RULER_HEADER_HEIGHT}px - ${theme.sizing.scale600})`,
  left: '0px',
  transform: 'translateX(-50%)',
  boxSizing: 'border-box',
  width: 'max-content',
  minWidth: theme.sizing.scale650,
  height: theme.sizing.scale600,
  whiteSpace: 'nowrap',
  paddingLeft: theme.sizing.scale100,
  paddingRight: theme.sizing.scale100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.colors.contentInversePrimary,
  backgroundColor: theme.colors.brandContentPrimary,
  borderRadius: theme.borders.radius200,
  // Only the badge is a grab handle: the vertical line stays pointer-events:none below
  // so it never intercepts clicks on spans/keyframes sitting at the current frame.
  pointerEvents: 'auto',
  cursor: MARKER_CURSOR.IDLE
});
export const CurrentFrameMarker = ({
  totalFrames,
  cellWidth = CELL_WIDTH,
  badgeRef
}: PropsT) => {
  const [css, theme] = useStyletron();
  const currentFrame = useAtomValue(currentFrameAtom);
  if (totalFrames <= 0) return null;
  return <div data-testid="current-frame-marker"
  /* Inline style, not css(): this value changes on every frame during a scrub, and
   styletron mints a new atomic class per distinct value passed to css() — a fast drag
   would otherwise flood the styletron cache with hundreds of one-off transform classes. */ style={{
    transform: getMarkerTransform(currentFrame, cellWidth)
  }} className={css(MARKER_STYLE)}>
      <div className={css(STICKY_LAYER_STYLE)}>
        <div className={css(getLineStyle(theme))} />
        <div ref={badgeRef} data-testid="current-frame-marker-badge" {...{
        [TIMELINE_DRAG_ATTR]: ''
      }} className={css(getBadgeStyle(theme))}>
          {formatFrameBadge(currentFrame)}
        </div>
      </div>
    </div>;
};